import { useEffect, useRef, useState } from 'react';
import { createElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HeartHandshake, Target, Lightbulb, Layers, CheckCircle2, MoveHorizontal, Check, ArrowLeft, ArrowRight, Navigation, Shield, User, Link2, Sparkles, Compass, Gauge, Zap } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { CASE_STUDY } from '@/constants/testIds';
import { proxyImg } from '@/lib/format';
import { useCaseStudyChapter } from '@/lib/caseStudyChapter';
import CloudHubIA from './CloudHubIA';
import CloudHubImpactDeck from './CloudHubImpactDeck';
import { CloudHubJourneyMap, CloudHubEmpathyMap } from './CloudHubEmpathise';
import { EngagedlyEmpathyMap } from './EngagedlyEmpathyMap';
import { EngagedlyWorkshopBoard } from './EngagedlyWorkshopBoard';
import { EngagedlyRoughSketch } from './EngagedlyRoughSketch';
import { EngagedlyTraits } from './EngagedlyTraits';

// Design-Thinking phase palette (used by `phase-hero` block).
// Colours are tuned to feel at home on a dark surface, accent as border/dot,
// tinted background is 10% opacity, icon container is the solid accent.
const PHASE_STYLES = {
    empathise: {
        Icon: HeartHandshake,
        accent: '#f472b6', // rose-400
        tintBg: 'rgba(244, 114, 182, 0.10)',
        ring: 'rgba(244, 114, 182, 0.35)',
    },
    define: {
        Icon: Target,
        accent: '#60a5fa', // sky-400
        tintBg: 'rgba(96, 165, 250, 0.10)',
        ring: 'rgba(96, 165, 250, 0.35)',
    },
    ideate: {
        Icon: Lightbulb,
        accent: '#facc15', // amber-400
        tintBg: 'rgba(250, 204, 21, 0.10)',
        ring: 'rgba(250, 204, 21, 0.35)',
    },
    prototype: {
        Icon: Layers,
        accent: '#c084fc', // fuchsia/violet-400
        tintBg: 'rgba(192, 132, 252, 0.10)',
        ring: 'rgba(192, 132, 252, 0.35)',
    },
    deliver: {
        Icon: CheckCircle2,
        accent: '#34d399', // emerald-400
        tintBg: 'rgba(52, 211, 153, 0.10)',
        ring: 'rgba(52, 211, 153, 0.35)',
    },
};

// ── Rich block renderers ────────────────────────────────────────────────────
// Used by `type: 'rich'` case studies. Composable, data-driven.

export const RichHeading = ({ heading, subheading, paragraph }) => (
    <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-6%' }}
        transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
        className="mb-6 md:mb-8 mt-6 md:mt-10"
    >
        {heading && (
            <h2 className="font-display leading-[0.98] text-3xl md:text-5xl text-[var(--ink)]">
                {heading}
                <span className="text-[var(--accent)]">.</span>
            </h2>
        )}
        {subheading && (
            <h3
                className={`font-display-italic text-xl md:text-2xl text-[var(--ink)] ${
                    heading ? 'mt-4' : ''
                }`}
            >
                {subheading}
            </h3>
        )}
        {paragraph && (
            <p className="mt-4 font-sans-brand text-[14.5px] md:text-[15px] leading-[1.65] text-[var(--ink-2)]">
                {paragraph}
            </p>
        )}
    </motion.div>
);

export const RichImage = ({ src, caption, size, variant }) => {
    // `size` constrains the rendered width so single-phone screens don't dominate
    // the layout next to multi-phone galleries. Passed via block data as
    // { kind: 'image', size: 'sm' | 'md' }.
    // `variant: 'sketch'` gives a subtle tilt so paper wireframes read as
    // scanned artefacts. Backgrounds are transparent, the images themselves
    // carry their content (phones, paper photos) and the theme surface shows
    // through, so both dark and light modes look clean.
    // `variant: 'phone'` adds a lightweight showcase stage, a spotlight
    // gradient behind and a soft ground shadow beneath, so pre-mocked phone
    // screens read as devices sitting on a design stage instead of flat
    // rectangles floating on the dark canvas.
    const sizeClass =
        size === 'sm' ? 'max-w-[280px] md:max-w-[320px]' :
        size === 'md' ? 'max-w-[520px] md:max-w-[620px]' :
        '';
    const isSketch = variant === 'sketch';
    const isPhone = variant === 'phone';
    const isHmi = variant === 'hmi';
    const tilt = isSketch ? (size === 'md' ? '1deg' : '-0.8deg') : '0deg';

    if (isHmi) {
        // Wraps a flat HMI screen in a subtle in-dash bezel so it reads as a
        // cabin display rather than a floating rectangle.
        return (
            <motion.figure
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-6%' }}
                transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
                className={`mb-6 md:mb-10 relative mx-auto ${sizeClass || 'max-w-[760px]'}`}
            >
                <div className="relative rounded-[20px] bg-[#0b0e0c] border border-[var(--line-2)] shadow-[0_22px_60px_-18px_rgba(0,0,0,0.7)] p-2 md:p-2.5">
                    <div className="rounded-[13px] overflow-hidden ring-1 ring-black/50">
                        <img
                            src={proxyImg(src)}
                            alt=""
                            loading="lazy"
                            className="block w-full h-auto"
                        />
                    </div>
                    {/* lime power indicator, hints it is a live display */}
                    <span
                        aria-hidden
                        className="absolute bottom-2 right-3 w-1.5 h-1.5 rounded-full bg-[var(--accent)] opacity-80 shadow-[0_0_8px_var(--accent)]"
                    />
                </div>
                {/* soft ground shadow under the dash mount */}
                <span
                    aria-hidden
                    className="pointer-events-none mx-auto mt-3 block h-3 w-[62%] rounded-full bg-black/55 blur-2xl opacity-60"
                />
                {caption && (
                    <figcaption className="mt-4 text-center font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                        {caption}
                    </figcaption>
                )}
            </motion.figure>
        );
    }

    if (isPhone) {
        return (
            <motion.figure
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-6%' }}
                transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
                className={`mb-6 md:mb-10 relative mx-auto ${sizeClass || 'max-w-[720px]'}`}
            >
                {/* Spotlight glow behind the phone, makes the pre-mocked
                     dark bezels stand out on our dark surface. */}
                <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 -z-10 blur-3xl opacity-60"
                    style={{
                        background:
                            'radial-gradient(60% 45% at 50% 55%, var(--accent) 0%, transparent 70%)',
                        mixBlendMode: 'soft-light',
                    }}
                />
                {/* Frame surround, feels like the phones are on a matte
                     design stage rather than pasted onto the page. */}
                <div className="relative rounded-[28px] border border-[var(--line)] bg-[var(--surface-2)]/60 backdrop-blur-[2px] p-4 md:p-6">
                    <img
                        src={proxyImg(src)}
                        alt=""
                        loading="lazy"
                        className="block w-full h-auto"
                    />
                </div>
                {/* Ground shadow, anchors the phones so they feel physical. */}
                <span
                    aria-hidden
                    className="pointer-events-none block mx-auto mt-4 h-4 w-[70%] rounded-full bg-black/60 blur-2xl opacity-70"
                />
                {caption && (
                    <figcaption className="mt-4 text-center font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                        {caption}
                    </figcaption>
                )}
            </motion.figure>
        );
    }

    return (
        <motion.figure
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-6%' }}
            transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
            className={`mb-6 md:mb-8 ${sizeClass ? 'mr-auto ' + sizeClass : ''}`}
            style={isSketch ? { transform: `rotate(${tilt})` } : undefined}
        >
            <img
                src={proxyImg(src)}
                alt=""
                loading="lazy"
                className="block w-full h-auto"
            />
            {caption && (
                <figcaption className={`mt-3 font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)]${sizeClass ? ' text-left' : ''}`}>
                    {caption}
                </figcaption>
            )}
        </motion.figure>
    );
};

const BlockImageGrid = ({ images }) => (
    <div className="mb-6 md:mb-8 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        {images.map((src, j) => (
            <figure
                key={j}
                className="group relative aspect-[4/3] rounded-xl overflow-hidden border border-[var(--line)] bg-[var(--surface-2)]"
            >
                <img
                    src={proxyImg(src)}
                    alt=""
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                />
                <span className="absolute top-2 left-2 font-mono-brand text-[9.5px] uppercase tracking-[0.22em] text-[var(--ink)] bg-[var(--bg)]/80 backdrop-blur px-2 py-1 rounded-full border border-[var(--line)] tabular-nums">
                    {String(j + 1).padStart(2, '0')}
                </span>
            </figure>
        ))}
    </div>
);

const GoalCard = ({ g, j }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-6%' }}
        transition={{ duration: 0.6, delay: j * 0.08 }}
        className="rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-5 md:p-6 flex flex-col gap-3"
    >
        <span className="font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--accent)] tabular-nums">
            {String(j + 1).padStart(2, '0')} · Goal
        </span>
        <h4 className="font-display-italic text-2xl md:text-3xl text-[var(--ink)] leading-tight">
            {g.title}
        </h4>
        <p className="font-sans-brand text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
            {g.body}
        </p>
    </motion.div>
);

const BlockGoals = ({ heading, items }) => (
    <div className="mb-6 md:mb-10">
        <RichHeading heading={heading} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            {items.map((g, j) => (
                <GoalCard key={j} g={g} j={j} />
            ))}
        </div>
    </div>
);

const BlockGridCards = ({ items }) => {
    // Choose column count so each row fills edge-to-edge on desktop.
    const gridCols =
        {
            1: 'md:grid-cols-1',
            2: 'md:grid-cols-2',
            3: 'md:grid-cols-3',
            4: 'md:grid-cols-4',
            5: 'md:grid-cols-5',
            6: 'md:grid-cols-3', // 2 tidy rows of 3
        }[items.length] || 'md:grid-cols-3';
    return (
        <div className={`mb-6 md:mb-8 grid grid-cols-2 ${gridCols} gap-3 md:gap-4`}>
            {items.map((c, j) => (
                <div
                    key={j}
                    className="rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-5 flex flex-col gap-2"
                >
                    <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                        {String(j + 1).padStart(2, '0')}
                    </span>
                    <h4 className="font-display text-xl text-[var(--ink)]">
                        {c.title}
                    </h4>
                    <p className="font-sans-brand text-[12.5px] leading-[1.5] text-[var(--ink-2)]">
                        {c.body}
                    </p>
                </div>
            ))}
        </div>
    );
};

// ── Colour palette swatches ─────────────────────────────────────────────────
// Renders each item as a big filled swatch using its own hex as background,
// with an auto-picked ink color for legibility. Data shape per item:
//   { name: 'Deep Blue', hex: '#5200FF', use: 'Primary brand + CTAs' }
const luminanceOf = (hex) => {
    const h = hex.replace('#', '').trim();
    if (h.length < 6) return 1;
    const r = parseInt(h.slice(0, 2), 16) / 255;
    const g = parseInt(h.slice(2, 4), 16) / 255;
    const b = parseInt(h.slice(4, 6), 16) / 255;
    const srgb = [r, g, b].map((v) =>
        v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
    );
    return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
};

const BlockColorSwatches = ({ items }) => (
    <div className="mb-6 md:mb-8 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        {items.map((c, j) => {
            const dark = luminanceOf(c.hex) < 0.55;
            const ink = dark ? '#FFFFFF' : '#0B0B0C';
            const inkSoft = dark ? 'rgba(255,255,255,0.72)' : 'rgba(11,11,12,0.7)';
            return (
                <div
                    key={j}
                    className="rounded-2xl overflow-hidden border border-[var(--line)] flex flex-col"
                >
                    <div
                        className="h-32 md:h-36 relative"
                        style={{ backgroundColor: c.hex }}
                    >
                        <span
                            className="absolute top-3 left-3 font-mono-brand text-[10px] uppercase tracking-[0.22em] tabular-nums"
                            style={{ color: inkSoft }}
                        >
                            {String(j + 1).padStart(2, '0')}
                        </span>
                        <span
                            className="absolute bottom-3 left-3 font-mono-brand text-[11px] uppercase tracking-[0.18em] tabular-nums"
                            style={{ color: ink }}
                        >
                            {c.hex}
                        </span>
                    </div>
                    <div className="bg-[var(--surface)] p-4 flex flex-col gap-1">
                        <h4 className="font-display text-lg text-[var(--ink)] leading-tight">
                            {c.name}
                        </h4>
                        {c.use && (
                            <p className="font-sans-brand text-[12px] leading-[1.5] text-[var(--ink-2)]">
                                {c.use}
                            </p>
                        )}
                    </div>
                </div>
            );
        })}
    </div>
);

const QuoteCard = ({ q, j }) => (
    <motion.blockquote
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-3%' }}
        transition={{ duration: 0.5, delay: (j % 4) * 0.06 }}
        className="rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-5 relative"
    >
        <span className="absolute -top-1 left-4 font-display-italic text-4xl text-[var(--accent)] opacity-40 leading-none">
            &ldquo;
        </span>
        <p className="font-sans-brand text-[13.5px] leading-[1.6] text-[var(--ink-2)] pl-4">
            {q}
        </p>
    </motion.blockquote>
);

const BlockQuotes = ({ items }) => (
    <div
        className={`mb-8 md:mb-10 grid grid-cols-1 gap-3 md:gap-4${
            items.length > 1 ? ' md:grid-cols-2' : ''
        }`}
    >
        {items.map((q, j) => (
            <QuoteCard key={j} q={q} j={j} />
        ))}
    </div>
);

const BlockMetaList = ({ items }) => (
    <div className="mb-6 md:mb-8 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-5 md:p-6 flex flex-col gap-5">
        {items.map((m, j) => (
            <div key={j}>
                <div className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                    {m.label}
                </div>
                <div className="mt-1.5 font-sans-brand text-[14px] leading-[1.6] text-[var(--ink)]">
                    {m.value}
                </div>
            </div>
        ))}
    </div>
);

const PersonaColumn = ({ title, items, tone }) => {
    if (!items || items.length === 0) return null;
    return (
        <div className="p-5 md:p-6 border-b md:border-b-0 md:border-r border-[var(--line)] last:border-r-0 last:border-b-0">
            <div className={`font-mono-brand text-[10px] uppercase tracking-[0.22em] ${tone}`}>
                {title}
            </div>
            <ul className="mt-3 space-y-2">
                {items.map((it, k) => (
                    <li
                        key={k}
                        className="font-sans-brand text-[12.5px] leading-[1.55] text-[var(--ink-2)] flex gap-2"
                    >
                        <span className={`shrink-0 ${tone}`}>·</span>
                        <span>{it}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const PersonaStats = ({ stats }) => (
    <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s, j) => (
            <div key={j}>
                <dt className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                    {s.label}
                </dt>
                <dd className="mt-1 font-sans-brand text-[13px] text-[var(--ink)]">
                    {s.value}
                </dd>
            </div>
        ))}
    </dl>
);

const BlockPersona = ({ block: b }) => {
    // Only render the columns that have items, makes the persona block
    // resilient to data files that don't provide every column (e.g. wishlist).
    const columns = [
        b.goals && b.goals.length > 0 && (
            <PersonaColumn key="goals" title="Goals" items={b.goals} tone="text-emerald-400" />
        ),
        b.wishlist && b.wishlist.length > 0 && (
            <PersonaColumn key="wishlist" title="Wishlist" items={b.wishlist} tone="text-[var(--accent)]" />
        ),
        b.frustrations && b.frustrations.length > 0 && (
            <PersonaColumn key="frustrations" title="Frustrations" items={b.frustrations} tone="text-[var(--warn)]" />
        ),
    ].filter(Boolean);
    const gridCols =
        columns.length === 1
            ? 'md:grid-cols-1'
            : columns.length === 2
              ? 'md:grid-cols-2'
              : 'md:grid-cols-3';

    return (
        <div className="mb-8 md:mb-12 rounded-[22px] border border-[var(--line)] bg-[var(--surface)] overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-[240px_1fr]">
                <div className="group relative bg-[var(--surface-2)] min-h-[240px] overflow-hidden">
                    <img
                        src={proxyImg(b.image)}
                        alt={b.name}
                        className="absolute inset-0 w-full h-full object-cover transition-[transform,filter] duration-[400ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:scale-[1.04] group-hover:brightness-[1.08] group-hover:saturate-[1.12]"
                    />
                </div>
                <div className="p-5 md:p-7 flex flex-col gap-4">
                    <div>
                        <div className="font-display-italic text-2xl md:text-3xl text-[var(--ink)]">
                            {b.name}
                        </div>
                        <p className="mt-2 font-sans-brand text-[13.5px] leading-[1.6] text-[var(--ink-2)]">
                            {b.intro}
                        </p>
                    </div>
                    <PersonaStats stats={b.stats} />
                </div>
            </div>
            {b.traitsImage && (
                <div className="border-t border-[var(--line)] p-5">
                    <img
                        src={proxyImg(b.traitsImage)}
                        alt="Personality traits"
                        loading="lazy"
                        className="block w-full h-auto rounded-xl"
                    />
                </div>
            )}
            {columns.length > 0 && (
                <div className={`grid grid-cols-1 ${gridCols} border-t border-[var(--line)]`}>
                    {columns}
                </div>
            )}
        </div>
    );
};

const BlockPrinciple = ({ block: b }) => (
    <div className="mb-6 md:mb-8 grid grid-cols-1 md:grid-cols-[1fr_1.4fr] gap-4 md:gap-6 items-start">
        <div className="rounded-2xl overflow-hidden border border-[var(--line)] bg-[var(--surface)]">
            <img
                src={proxyImg(b.image)}
                alt={b.heading}
                loading="lazy"
                className="block w-full h-auto"
            />
        </div>
        <div className="rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-5 md:p-6 flex flex-col gap-3">
            <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--accent)]">
                Design principle
            </span>
            <h3 className="font-display-italic text-2xl md:text-3xl text-[var(--ink)] leading-tight">
                {b.heading}
            </h3>
            <p className="font-sans-brand text-[13.5px] leading-[1.65] text-[var(--ink-2)]">
                {b.body}
            </p>
        </div>
    </div>
);

const ExcludedCard = ({ item: f }) => (
    <div className="rounded-2xl border border-dashed border-[var(--line-2)] bg-[var(--surface)] overflow-hidden flex flex-col">
        <div className="bg-[var(--surface-2)]">
            <img
                src={proxyImg(f.image)}
                alt={f.title}
                loading="lazy"
                className="block w-full h-auto"
            />
        </div>
        <div className="p-5 flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
                <h4 className="font-display-italic text-xl text-[var(--ink)]">
                    {f.title}
                </h4>
                <span className="font-mono-brand text-[9.5px] uppercase tracking-[0.22em] text-[var(--warn)]">
                    Excluded
                </span>
            </div>
            <div>
                <div className="font-mono-brand text-[9.5px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                    Effort vs Value
                </div>
                <p className="mt-1 font-sans-brand text-[12.5px] leading-[1.55] text-[var(--ink-2)]">
                    {f.effort}
                </p>
            </div>
            <div>
                <div className="font-mono-brand text-[9.5px] uppercase tracking-[0.22em] text-[var(--accent)]">
                    Alt / Remark
                </div>
                <p className="mt-1 font-sans-brand text-[12.5px] leading-[1.55] text-[var(--ink-2)]">
                    {f.alt}
                </p>
            </div>
        </div>
    </div>
);

const BlockExcluded = ({ items }) => (
    <div className="mb-8 md:mb-10 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
        {items.map((f, j) => (
            <ExcludedCard key={j} item={f} />
        ))}
    </div>
);

// Bullets, compact 2/3-column list, used for short "goals / list" content.
const BlockBullets = ({ heading, items, columns = 3 }) => (
    <div className="mb-6 md:mb-10">
        {heading && <RichHeading heading={heading} />}
        <ul
            className={`grid grid-cols-1 sm:grid-cols-2 ${
                columns === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'
            } gap-x-6 gap-y-3 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-5 md:p-6`}
        >
            {items.map((it, j) => (
                <li
                    key={j}
                    className="flex items-start gap-3 font-sans-brand text-[13.5px] leading-[1.55] text-[var(--ink)]"
                >
                    <span className="mt-[7px] w-1.5 h-1.5 rounded-full shrink-0 bg-[var(--accent)]" />
                    <span>{it}</span>
                </li>
            ))}
        </ul>
    </div>
);

// ── Storyboard ──────────────────────────────────────────────────────────────
// A "day in the life" journey storyboard. Each stage card carries an image plus
// three tone-coded groups: current experience (neutral), challenges (caution),
// opportunity (lime). Data shape per stage:
//   { title, subtitle, image, groups: [{ label, tone, items: [] }] }
const STORY_TONES = {
    current: { label: 'var(--ink-3)', dot: 'var(--ink-3)' },
    challenge: { label: '#F5A623', dot: '#F5A623' },
    opportunity: { label: 'var(--accent)', dot: 'var(--accent)' },
};

const StoryGroup = ({ label, tone = 'current', items = [] }) => {
    const t = STORY_TONES[tone] || STORY_TONES.current;
    return (
        <div>
            <div
                className="font-mono-brand text-[10px] uppercase tracking-[0.22em] mb-2"
                style={{ color: t.label }}
            >
                {label}
            </div>
            <ul className="flex flex-col gap-1.5">
                {items.map((it, k) => (
                    <li
                        key={k}
                        className="flex gap-2 font-sans-brand text-[12.5px] leading-[1.5] text-[var(--ink-2)]"
                    >
                        <span
                            className="mt-[7px] shrink-0 w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: t.dot }}
                        />
                        <span>{it}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const BlockStoryboard = ({ heading, scenario, stages = [] }) => (
    <div className="mb-6 md:mb-10">
        {(heading || scenario) && (
            <div className="mb-5 md:mb-7">
                {heading && (
                    <h3 className="font-display text-2xl md:text-3xl text-[var(--ink)]">
                        {heading}
                        <span className="text-[var(--accent)]">.</span>
                    </h3>
                )}
                {scenario && (
                    <p className="mt-3 font-sans-brand text-[13.5px] md:text-[14.5px] leading-[1.6] text-[var(--ink-2)]">
                        {scenario}
                    </p>
                )}
            </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {stages.map((s, i) => (
                <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 22 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-6%' }}
                    transition={{ duration: 0.55, ease: [0.2, 0.8, 0.2, 1], delay: (i % 3) * 0.06 }}
                    className="group flex flex-col rounded-2xl border border-[var(--line)] bg-[var(--surface)] overflow-hidden"
                >
                    {s.image && (
                        <div className="relative aspect-[16/10] overflow-hidden">
                            <img
                                src={proxyImg(s.image)}
                                alt=""
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.08]"
                            />
                            <span className="absolute top-3 left-3 grid place-items-center w-7 h-7 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] font-mono-brand text-[12px] tabular-nums">
                                {i + 1}
                            </span>
                        </div>
                    )}
                    <div className="p-5 flex flex-col gap-4">
                        <div>
                            <h4 className="font-display text-xl text-[var(--ink)]">{s.title}</h4>
                            {s.subtitle && (
                                <div className="mt-1 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--accent)]">
                                    {s.subtitle}
                                </div>
                            )}
                        </div>
                        {(s.groups || []).map((g, k) => (
                            <StoryGroup key={k} {...g} />
                        ))}
                        {s.moduleLabel && (
                            <button
                                type="button"
                                data-cursor="hover"
                                data-testid={`storyboard-see-solution-${i}`}
                                onClick={() => {
                                    window.__csPendingSubtab = s.moduleLabel;
                                    window.dispatchEvent(
                                        new CustomEvent('cs-goto-tab', {
                                            detail: { label: 'Proposed Experience' },
                                        })
                                    );
                                    setTimeout(
                                        () =>
                                            window.dispatchEvent(
                                                new CustomEvent('cs-goto-subtab', {
                                                    detail: { label: s.moduleLabel },
                                                })
                                            ),
                                        60
                                    );
                                }}
                                className="group/btn mt-1 inline-flex items-center gap-1.5 self-start font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[var(--accent)] transition-all hover:gap-2.5"
                            >
                                See the solution
                                <span aria-hidden className="transition-transform group-hover/btn:translate-x-0.5">
                                    &rarr;
                                </span>
                            </button>
                        )}
                    </div>
                </motion.div>
            ))}
        </div>
    </div>
);


// Phase-hero, a coloured Design-Thinking phase callout used at the top of
// each tab in a Design-Thinking-structured case study.
const BlockPhaseHero = ({ block: b }) => {
    const style = PHASE_STYLES[b.phase] || PHASE_STYLES.empathise;
    const { Icon } = style;
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
            className="mb-8 md:mb-12 rounded-[22px] p-6 md:p-8 border overflow-hidden relative"
            style={{
                background: style.tintBg,
                borderColor: style.ring,
            }}
        >
            <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-8 items-start">
                <div
                    className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-2xl shrink-0"
                    style={{
                        background: style.accent,
                        color: '#0a0a0a',
                    }}
                >
                    <Icon size={32} strokeWidth={2} />
                </div>
                <div className="flex flex-col gap-4">
                    <div>
                        <div
                            className="font-mono-brand text-[10.5px] uppercase tracking-[0.22em] tabular-nums"
                            style={{ color: style.accent }}
                        >
                            Phase {b.number} · {String(b.title).toLowerCase()}
                        </div>
                        {b.tagline && (
                            <p className="mt-3 font-sans-brand text-[14.5px] md:text-[15px] leading-[1.65] text-[var(--ink-2)]">
                                {b.tagline}
                            </p>
                        )}
                    </div>
                    {b.items && b.items.length > 0 && (
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-1">
                            {b.items.map((it, j) => (
                                <li
                                    key={j}
                                    className="flex items-start gap-3 font-sans-brand text-[13px] leading-[1.55] text-[var(--ink)]"
                                >
                                    <span
                                        className="mt-[7px] w-1.5 h-1.5 rounded-full shrink-0"
                                        style={{ background: style.accent }}
                                    />
                                    <span>{it}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

// ── Tabs, top-level chapter navigation for dense case studies. ─────────────
// State is in-page only (does not sync with URL) to keep behaviour lightweight.
// Presentation: a numbered stepper with connecting lines between nodes.
// The active node fills lime, the completed line up to it turns lime, and each
// step's label sits directly below the node so viewers read the whole design
// process as a journey rather than a set of pills.

const TabStepper = ({ tabs, active, onSelect }) => (
    <div
        role="tablist"
        aria-label="Case study chapters"
        className="relative flex items-start justify-between gap-1 md:gap-4 py-4 md:py-6 min-w-max md:min-w-0"
    >
        {tabs.map((t, i) => {
            const isActive = active === i;
            const isDone = active > i;
            const isLast = i === tabs.length - 1;
            return (
                <div
                    key={i}
                    className="relative flex-1 flex flex-col items-center min-w-[68px] md:min-w-0"
                >
                    {/* connector line to the next node, sits behind the node */}
                    {!isLast && (
                        <div
                            aria-hidden="true"
                            className="absolute top-[15px] md:top-[23px] left-1/2 w-full h-[2px] -z-0"
                        >
                            <div className="w-full h-full bg-[var(--line-2)]" />
                            <motion.div
                                className="absolute inset-0 h-full bg-[var(--accent)] origin-left"
                                initial={false}
                                animate={{ scaleX: isDone ? 1 : 0 }}
                                transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
                            />
                        </div>
                    )}

                    <button
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        onClick={() => onSelect(i)}
                        data-testid={CASE_STUDY.tabTrigger(i)}
                        data-cursor="hover"
                        className="relative z-10 flex flex-col items-center gap-1.5 md:gap-2 group focus:outline-none"
                    >
                        {/* the node, bigger circular pill with number inside */}
                        <motion.span
                            className={`relative flex items-center justify-center w-8 h-8 md:w-12 md:h-12 rounded-full font-mono-brand text-[11px] md:text-[14px] font-semibold tabular-nums border transition-colors ${
                                isActive
                                    ? 'bg-[var(--accent)] text-[var(--accent-ink)] border-transparent shadow-[0_0_0_6px_var(--accent-glow)]'
                                    : isDone
                                        ? 'bg-transparent text-[var(--accent)] border-[var(--accent)]'
                                        : 'bg-transparent text-[var(--ink-3)] border-[var(--line-2)] group-hover:text-[var(--ink)] group-hover:border-[var(--line-3,#3a3a3d)]'
                            }`}
                            animate={{ scale: isActive ? 1.06 : 1 }}
                            transition={{ type: 'spring', stiffness: 260, damping: 18 }}
                        >
                            {String(i + 1).padStart(2, '0')}
                        </motion.span>
                        {/* label under the node */}
                        <span
                            className={`font-mono-brand text-[9.5px] md:text-[11px] uppercase tracking-[0.14em] md:tracking-[0.22em] transition-colors whitespace-nowrap ${
                                isActive
                                    ? 'text-[var(--ink)]'
                                    : isDone
                                        ? 'text-[var(--ink-2)]'
                                        : 'text-[var(--ink-3)] group-hover:text-[var(--ink-2)]'
                            }`}
                        >
                            {t.label}
                        </span>
                    </button>
                </div>
            );
        })}
    </div>
);

// ────────────────────────────────────────────────────────────
// autoBlurb, walk a chapter's blocks to find a short one-line
// description so the ChapterCards below can preview each phase
// without every case-study data file having to author a `blurb`
// field. Priority: `phase-hero.body` → first `section.body` →
// first paragraph string encountered → fallback empty string.
// ────────────────────────────────────────────────────────────
const _MAX_BLURB = 110;

const _trim = (s) => {
    if (!s || typeof s !== 'string') return '';
    const clean = s.replace(/\s+/g, ' ').trim();
    if (clean.length <= _MAX_BLURB) return clean;
    return clean.slice(0, _MAX_BLURB).replace(/[,;:.\s]*\S*$/, '') + '…';
};

const autoBlurb = (blocks) => {
    if (!Array.isArray(blocks)) return '';
    for (const b of blocks) {
        if (!b || typeof b !== 'object') continue;
        if (b.kind === 'phase-hero' && (b.body || b.tagline)) return _trim(b.body || b.tagline);
        if (b.kind === 'section' && (b.paragraph || b.body || b.subtitle)) {
            return _trim(b.paragraph || b.body || b.subtitle);
        }
        if (b.kind === 'paragraph' && typeof b.text === 'string') {
            return _trim(b.text);
        }
        if (b.kind === 'callout' && b.body) return _trim(b.body);
    }
    return '';
};

// ────────────────────────────────────────────────────────────
// ChapterCards, the Kanban-style chapter picker. Each chapter
// is its own card with a number, title and a short blurb; the
// active card scales up, gains a lime border + accent bar; done
// chapters get a subtle check mark. Falls back to a scrollable
// horizontal strip on mobile.
//
// The desktop grid adapts to the number of chapters so the cards
// stay legible: 5 columns for shorter narratives (≤10) and 7
// columns for the extended 14-chapter narrative used by flagship
// enterprise case studies.
// ────────────────────────────────────────────────────────────
const ChapterCards = ({ tabs, active, onSelect }) => {
    const cols = tabs.length > 10
        ? 'md:grid-cols-7'
        : tabs.length > 7
            ? 'md:grid-cols-4'
            : 'md:grid-cols-5';
    return (
    <div
        role="tablist"
        aria-label="Case study chapters"
        className={`py-4 md:py-5 grid grid-cols-2 ${cols} gap-3 md:gap-4`}
    >
        {tabs.map((t, i) => {
            const isActive = active === i;
            const isDone = active > i;
            const blurb = t.blurb || autoBlurb(t.blocks);
            return (
                <motion.button
                    key={i}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => onSelect(i)}
                    data-testid={CASE_STUDY.tabTrigger(i)}
                    data-cursor="hover"
                    initial={false}
                    animate={{
                        scale: isActive ? 1.02 : 1,
                        y: isActive ? -2 : 0,
                    }}
                    transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                    className={`relative snap-start text-left rounded-xl border overflow-hidden group focus:outline-none transition-colors ${
                        isActive
                            ? 'border-[var(--accent)] bg-[color-mix(in_oklab,var(--accent)_5%,var(--surface))] shadow-[0_10px_30px_-15px_var(--accent-glow)]'
                            : isDone
                                ? 'border-[var(--line-2)] bg-[var(--surface)] hover:border-[var(--line-3,#3a3a3d)]'
                                : 'border-[var(--line)] bg-[var(--surface)] hover:border-[var(--line-2)]'
                    }`}
                >
                    {/* Top strip, chapter number + status */}
                    <div className="px-4 pt-3 flex items-center justify-between gap-2">
                        <span
                            className={`font-mono-brand text-[10px] tracking-[0.22em] uppercase tabular-nums ${
                                isActive
                                    ? 'text-[var(--accent)]'
                                    : 'text-[var(--ink-3)]'
                            }`}
                        >
                            Chapter {String(i + 1).padStart(2, '0')}
                        </span>
                        <span
                            className={`inline-flex items-center gap-1 text-[10px] font-mono-brand uppercase tracking-[0.16em] ${
                                isDone
                                    ? 'text-[var(--accent)]'
                                    : isActive
                                        ? 'text-[var(--ink)]'
                                        : 'text-[var(--ink-3)]'
                            }`}
                        >
                            {isDone ? (
                                <>
                                    <Check size={11} strokeWidth={2.4} /> Read
                                </>
                            ) : isActive ? (
                                <>
                                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] pulse-dot" />
                                    Now
                                </>
                            ) : (
                                <>Up next</>
                            )}
                        </span>
                    </div>

                    {/* Title */}
                    <div
                        className={`px-4 mt-1 font-display leading-tight text-[19px] md:text-[20px] ${
                            isActive ? 'text-[var(--ink)]' : 'text-[var(--ink)]'
                        }`}
                    >
                        {t.label}
                    </div>

                    {/* Blurb */}
                    {blurb && (
                        <div className="px-4 pt-1.5 pb-4 font-sans-brand text-[12px] leading-[1.5] text-[var(--ink-2)]">
                            <span className="line-clamp-2">{blurb}</span>
                        </div>
                    )}
                    {!blurb && <div className="pb-4" />}

                    {/* Bottom accent bar, grows to full width on active */}
                    <motion.span
                        aria-hidden="true"
                        className="absolute left-0 bottom-0 h-[3px] bg-[var(--accent)] origin-left"
                        initial={false}
                        animate={{
                            width: '100%',
                            scaleX: isActive ? 1 : isDone ? 1 : 0,
                            opacity: isActive ? 1 : isDone ? 0.55 : 0,
                        }}
                        transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
                    />
                </motion.button>
            );
        })}
    </div>
    );
};

// Slugify a tab label (e.g. "User research" → "user-research"). Used as the
// value of the `?tab=` URL query so recruiters can share a direct link to
// any chapter of a case study.
const slugifyTabLabel = (label) =>
    (label || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

// ────────────────────────────────────────────────────────────
// ChapterRail, the primary chapter navigation. A vertical timeline
// on desktop (sticky beside the content) and a horizontal, sticky
// scroll strip on mobile. Completed + active chapters show a lime
// check node; upcoming chapters a quiet dot. Shared by every rich
// case study.
// ────────────────────────────────────────────────────────────
const ChapterRail = ({ tabs, active, onSelect }) => {
    const listRef = useRef(null);
    // Keep the active chip centred in the mobile horizontal strip.
    useEffect(() => {
        const list = listRef.current;
        if (!list) return;
        const btn = list.children[active];
        if (!btn) return;
        const target = btn.offsetLeft - (list.clientWidth - btn.clientWidth) / 2;
        list.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }, [active]);
    return (
    <nav
        aria-label="Case study chapters"
        className="mb-6 lg:mb-0 sticky top-16 lg:top-24 lg:self-start z-20"
    >
        <div className="hidden lg:flex items-center gap-2 mb-5 font-mono-brand text-[10.5px] uppercase tracking-[0.24em] text-[var(--ink-3)]">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)]" />
            Case study
        </div>
        <div className="-mx-4 px-4 lg:mx-0 lg:px-0 z-20 bg-[var(--bg)]/90 backdrop-blur lg:bg-transparent lg:backdrop-blur-none border-b border-[var(--line)] lg:border-0 py-2 lg:py-0">
            <div
                ref={listRef}
                role="tablist"
                aria-label="Case study chapters"
                className="relative flex gap-2 overflow-x-auto lg:flex-col lg:gap-0 lg:overflow-visible"
            >
                {tabs.map((t, i) => {
                    const isActive = active === i;
                    const isDone = active > i;
                    const isLast = i === tabs.length - 1;
                    return (
                        <button
                            key={i}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            onClick={() => onSelect(i)}
                            data-testid={CASE_STUDY.tabTrigger(i)}
                            data-cursor="hover"
                            className={`group relative shrink-0 lg:w-full flex items-center gap-3 rounded-xl px-3 py-2.5 lg:py-3 text-left border transition-colors focus:outline-none ${
                                isActive
                                    ? 'border-[var(--accent)] bg-[color-mix(in_oklab,var(--accent)_7%,var(--surface))]'
                                    : 'border-transparent hover:bg-[var(--surface)]'
                            }`}
                        >
                            {/* desktop connector down to the next node */}
                            {!isLast && (
                                <span
                                    aria-hidden
                                    className={`hidden lg:block absolute left-[26px] top-1/2 h-full w-px ${
                                        isDone ? 'bg-[var(--accent)]' : 'bg-[var(--line-2)]'
                                    }`}
                                />
                            )}
                            {/* node */}
                            <span
                                className={`relative z-10 grid place-items-center w-7 h-7 rounded-full shrink-0 border transition-colors ${
                                    isActive
                                        ? 'bg-[var(--accent)] border-transparent text-[var(--accent-ink)]'
                                        : isDone
                                            ? 'bg-[var(--bg)] border-[var(--accent)] text-[var(--accent)]'
                                            : 'bg-[var(--bg)] border-[var(--line-2)] text-[var(--ink-3)]'
                                }`}
                            >
                                {isActive || isDone ? (
                                    <Check size={14} strokeWidth={2.6} />
                                ) : (
                                    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-50" />
                                )}
                            </span>
                            {/* number + label */}
                            <span className="min-w-0 leading-tight">
                                <span
                                    className={`block font-mono-brand text-[10px] tracking-[0.2em] tabular-nums ${
                                        isActive ? 'text-[var(--accent)]' : 'text-[var(--ink-3)]'
                                    }`}
                                >
                                    {String(i + 1).padStart(2, '0')}
                                </span>
                                <span
                                    className={`block font-sans-brand text-[13px] md:text-sm whitespace-nowrap lg:whitespace-normal ${
                                        isActive
                                            ? 'text-[var(--ink)] font-medium'
                                            : 'text-[var(--ink-2)] group-hover:text-[var(--ink)]'
                                    }`}
                                >
                                    {t.label}
                                </span>
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    </nav>
    );
};

// Bottom chapter navigation, previous / progress / next.
const ChapterFooterNav = ({ tabs, active, onSelect }) => {
    const prev = active > 0 ? tabs[active - 1] : null;
    const next = active < tabs.length - 1 ? tabs[active + 1] : null;
    const pct = ((active + 1) / tabs.length) * 100;
    return (
        <div className="mt-10 md:mt-14 pt-6 border-t border-[var(--line)] flex items-center justify-between gap-4">
            {prev ? (
                <button
                    type="button"
                    onClick={() => onSelect(active - 1)}
                    data-testid="case-study-chapter-prev"
                    data-cursor="hover"
                    className="group flex items-center gap-3 text-left focus:outline-none"
                >
                    <span className="grid place-items-center w-11 h-11 rounded-full border border-[var(--line-2)] text-[var(--ink)] group-hover:border-[var(--accent)] group-hover:text-[var(--accent)] transition-colors">
                        <ArrowLeft size={18} />
                    </span>
                    <span className="hidden sm:block">
                        <span className="block font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[var(--ink-3)]">
                            Previous chapter
                        </span>
                        <span className="block font-display text-base text-[var(--ink)]">{prev.label}</span>
                    </span>
                </button>
            ) : (
                <span />
            )}

            <div className="flex items-center gap-3 shrink-0">
                <div className="w-20 sm:w-32 h-1 rounded-full bg-[var(--line-2)] overflow-hidden">
                    <motion.div
                        className="h-full rounded-full bg-[var(--accent)]"
                        initial={false}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
                    />
                </div>
                <span className="font-mono-brand text-[11px] tabular-nums text-[var(--ink-3)]">
                    {String(active + 1).padStart(2, '0')} / {String(tabs.length).padStart(2, '0')}
                </span>
            </div>

            {next ? (
                <button
                    type="button"
                    onClick={() => onSelect(active + 1)}
                    data-testid="case-study-chapter-next"
                    data-cursor="hover"
                    className="group flex items-center gap-3 text-right focus:outline-none"
                >
                    <span className="hidden sm:block">
                        <span className="block font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[var(--ink-3)]">
                            Next chapter
                        </span>
                        <span className="block font-display text-base text-[var(--ink)]">{next.label}</span>
                    </span>
                    <span className="grid place-items-center w-11 h-11 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] group-hover:brightness-95 transition-all shadow-[0_8px_24px_-10px_var(--accent-glow)]">
                        <ArrowRight size={18} />
                    </span>
                </button>
            ) : (
                <span />
            )}
        </div>
    );
};

// Walk a chapter's blocks (incl. nested tabs / storyboard) for a usable image,
// used as a hero / filmstrip-thumbnail fallback when none is authored.
const findFirstImage = (blocks) => {
    if (!Array.isArray(blocks)) return null;
    for (const b of blocks) {
        if (!b || typeof b !== 'object') continue;
        if (b.kind === 'image' && b.src) return b.src;
        if (b.kind === 'storyboard' && Array.isArray(b.stages)) {
            const st = b.stages.find((s) => s.image);
            if (st) return st.image;
        }
        if ((b.kind === 'sub-tabs' || b.kind === 'tabs') && Array.isArray(b.tabs)) {
            for (const t of b.tabs) {
                const found = findFirstImage(t.blocks);
                if (found) return found;
            }
        }
        if (b.kind === 'image-grid' && Array.isArray(b.images) && b.images[0]) {
            return typeof b.images[0] === 'string' ? b.images[0] : b.images[0].src;
        }
    }
    return null;
};

const HILITE_ICONS = { navigation: Navigation, shield: Shield, user: User, link: Link2, sparkles: Sparkles, compass: Compass, gauge: Gauge, zap: Zap };

// Generic, on-brand abstract banners used as chapter heroes / thumbnails for
// studies that would otherwise fall back to raw UI screenshots. A per-study
// offset keeps two studies from opening on the same banner.
const GENERIC_BANNERS = [
    'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/1fd0dbfb80fa6d75b68d749ea8c03a386396e613932d55b1d3a442e82d88fa36.jpeg',
    'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/a4cf9029217b883b7793e070ad1c3d8d9eacaf22b60cb0cad6538612bd0c77a7.jpeg',
    'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/f8535e5a9772ae8fe9bc48fe144e4ff84b44a9d52ffbf113fefcb47329cbf87a.jpeg',
    'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/40e92def2767d2892d99749ca63a3fa609ca859f047e63b9ef8beee27e181d4f.jpeg',
    'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/5a02afd003a2fa476087712dae815b2b73582b23cbd57a7ad290962a58bbf123.jpeg',
    'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/32287fd6ff0903740c8d2f4c7e07cd0a44957f7dae4de4261afd39713821379a.jpeg',
];

// Top bar, chapter counter + segmented progress + prev / next.
const ChapterTopBar = ({ active, total, hasPrev, hasNext, onPrev, onNext }) => (
    <div className="sticky top-16 md:top-20 z-20 -mx-4 md:mx-0 mb-6 md:mb-8 px-4 md:px-0 py-3 backdrop-blur border-b border-[var(--line)] flex items-center justify-between gap-4" style={{ backgroundColor: 'color-mix(in srgb, var(--bg) 96%, transparent)' }}>
        <div className="flex items-center gap-3 min-w-0">
            <span className="font-mono-brand text-sm tabular-nums text-[var(--ink)]">
                {String(active + 1).padStart(2, '0')} <span className="text-[var(--ink-3)]">/ {String(total).padStart(2, '0')}</span>
            </span>
            <div className="hidden sm:flex items-center gap-1.5">
                {Array.from({ length: total }).map((_, i) => (
                    <span key={i} className={`h-1 rounded-full transition-all duration-300 ${i <= active ? 'w-8 bg-[var(--accent)]' : 'w-4 bg-[var(--line-2)]'}`} />
                ))}
            </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
            <button type="button" onClick={onPrev} disabled={!hasPrev} data-testid="case-study-chapter-prev" data-cursor="hover" aria-label="Previous chapter" className="grid place-items-center w-10 h-10 rounded-full border border-[var(--line-2)] text-[var(--ink)] disabled:opacity-30 disabled:cursor-not-allowed hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors">
                <ArrowLeft size={17} />
            </button>
            <button type="button" onClick={onNext} disabled={!hasNext} data-testid="case-study-chapter-next" data-cursor="hover" aria-label="Next chapter" className="grid place-items-center w-10 h-10 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] disabled:opacity-30 disabled:cursor-not-allowed hover:brightness-95 transition-all">
                <ArrowRight size={17} />
            </button>
        </div>
    </div>
);

// Chapter cover, full-width hero (image or themed gradient) + title +
// summary + explore CTA, with an optional row of chapter highlights.
const ChapterCover = ({ active, chapter, summary, heroImage, highlights = [], onExplore }) => (
    <div className="mb-6 md:mb-10">
        <div className={`relative overflow-hidden rounded-[22px] border border-[var(--line)] ${heroImage ? '' : 'bg-gradient-to-br from-[var(--surface-2)] to-[var(--surface)]'}`}>
            {heroImage ? (
                <>
                    <img src={proxyImg(heroImage)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#07100a]/95 via-[#07100a]/70 to-[#07100a]/25" />
                </>
            ) : (
                <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(120% 90% at 85% 12%, var(--accent-glow), transparent 55%)' }} />
            )}
            <div className="relative px-6 md:px-10 py-9 md:py-16 max-w-[640px]">
                <div className={`font-mono-brand text-[11px] uppercase tracking-[0.24em] mb-3 md:mb-4 ${heroImage ? 'text-[#D8FE5B]' : 'text-[var(--accent)]'}`}>
                    Chapter {String(active + 1).padStart(2, '0')}
                </div>
                <AnimatePresence mode="wait">
                    <motion.h2
                        key={`cover-${active}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
                        className={`font-display leading-[0.95] tracking-tight text-4xl md:text-6xl ${heroImage ? 'text-white' : 'text-[var(--ink)]'}`}
                    >
                        {chapter.label}
                        <span className={heroImage ? 'text-[#D8FE5B]' : 'text-[var(--accent)]'}>.</span>
                    </motion.h2>
                </AnimatePresence>
                {summary && (
                    <p className={`mt-4 md:mt-5 font-sans-brand text-sm md:text-base leading-[1.6] max-w-[46ch] ${heroImage ? 'text-white/80' : 'text-[var(--ink-2)]'}`}>
                        {summary}
                    </p>
                )}
                <button type="button" onClick={onExplore} data-testid="case-study-chapter-explore" data-cursor="hover" className={`group mt-6 md:mt-8 inline-flex items-center gap-2.5 rounded-full font-medium text-sm px-5 py-2.5 hover:gap-3.5 transition-all ${heroImage ? 'bg-[#D8FE5B] text-[#0b0b0c]' : 'bg-[var(--accent)] text-[var(--accent-ink)]'}`}>
                    Explore this chapter
                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                </button>
            </div>
        </div>
        {highlights.length > 0 && (
            <div className="mt-6 md:mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {highlights.map((h, i) => {
                    const Icon = HILITE_ICONS[h.icon] || Sparkles;
                    return (
                        <div key={i} className="flex items-start gap-3">
                            <span className="grid place-items-center w-11 h-11 rounded-full border-2 border-[var(--accent)] text-[var(--accent)] shrink-0">
                                <Icon size={18} />
                            </span>
                            <div className="min-w-0">
                                <div className="font-display text-base text-[var(--ink)]">{h.title}</div>
                                <div className="mt-0.5 font-sans-brand text-[13px] leading-[1.5] text-[var(--ink-2)]">{h.body}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        )}
    </div>
);

// Bottom filmstrip, horizontal chapter thumbnails + a "coming up next" card.
const ChapterFilmstrip = ({ tabs, active, onSelect, thumbs }) => {
    const nextIdx = active < tabs.length - 1 ? active + 1 : null;
    return (
        <div className="mt-8 md:mt-12 pt-6 border-t border-[var(--line)]">
            <div className="relative">
            <div className="flex gap-3 md:gap-4 overflow-x-auto pb-2">
                {tabs.map((t, i) => {
                    const isActive = i === active;
                    const thumb = thumbs[i];
                    return (
                        <button
                            key={i}
                            type="button"
                            onClick={() => onSelect(i)}
                            data-testid={CASE_STUDY.tabTrigger(i)}
                            data-cursor="hover"
                            aria-selected={isActive}
                            className={`group shrink-0 w-[146px] md:w-[164px] text-left rounded-xl overflow-hidden border transition-colors ${isActive ? 'border-[var(--accent)]' : 'border-[var(--line)] hover:border-[var(--line-2)]'}`}
                        >
                            <div className="relative aspect-[16/10] overflow-hidden bg-[var(--surface-2)]">
                                {thumb ? (
                                    <img src={proxyImg(thumb)} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                ) : (
                                    <div className="absolute inset-0 grid place-items-center">
                                        <span className="font-display text-3xl text-[var(--ink-3)]">{String(i + 1).padStart(2, '0')}</span>
                                    </div>
                                )}
                                {isActive && <span aria-hidden className="absolute inset-0 rounded-xl ring-2 ring-inset ring-[var(--accent)]" />}
                            </div>
                            <div className="p-3">
                                <div className={`font-mono-brand text-[10px] tabular-nums ${isActive ? 'text-[var(--accent)]' : 'text-[var(--ink-3)]'}`}>{String(i + 1).padStart(2, '0')}</div>
                                <div className={`font-sans-brand text-[13px] leading-tight whitespace-nowrap overflow-hidden text-ellipsis ${isActive ? 'text-[var(--ink)] font-medium' : 'text-[var(--ink-2)]'}`}>{t.label}</div>
                                {isActive && <span className="mt-1.5 block h-0.5 w-10 bg-[var(--accent)] rounded-full" />}
                            </div>
                        </button>
                    );
                })}
                {nextIdx !== null && (
                    <button
                        type="button"
                        onClick={() => onSelect(nextIdx)}
                        data-cursor="hover"
                        data-testid="case-study-coming-next"
                        className="group shrink-0 w-[190px] self-stretch flex items-center justify-between gap-3 rounded-xl border border-[var(--line)] hover:border-[var(--accent)] px-4 py-4 transition-colors"
                    >
                        <div className="min-w-0">
                            <div className="font-mono-brand text-[10px] uppercase tracking-[0.18em] text-[var(--ink-3)]">Coming up next</div>
                            <div className="mt-1 font-sans-brand text-[13px] text-[var(--ink)] whitespace-nowrap overflow-hidden text-ellipsis">
                                {String(nextIdx + 1).padStart(2, '0')} {tabs[nextIdx].label}
                            </div>
                        </div>
                        <span className="grid place-items-center w-10 h-10 rounded-full border border-[var(--line-2)] text-[var(--ink)] group-hover:border-[var(--accent)] group-hover:text-[var(--accent)] transition-colors shrink-0">
                            <ArrowRight size={16} />
                        </span>
                    </button>
                )}
            </div>
            <span aria-hidden className="pointer-events-none absolute top-0 right-0 bottom-2 w-12 bg-gradient-to-l from-[var(--bg)] to-transparent" />
            </div>
        </div>
    );
};

const RichTabs = ({ tabs }) => {
    // Build stable slugs once per tabs array
    const slugs = tabs.map((t) => slugifyTabLabel(t.label));

    const [searchParams, setSearchParams] = useSearchParams();
    const initialSlug = searchParams.get('tab');
    const initialIdx = Math.max(
        0,
        initialSlug ? slugs.indexOf(initialSlug) : 0
    );

    const [active, setActive] = useState(initialIdx);
    const mountedRef = useRef(false);
    const sectionRef = useRef(null);
    const contentRef = useRef(null);
    const { setChapter, clearChapter } = useCaseStudyChapter();

    const exploreChapter = () => {
        const el = contentRef.current;
        if (!el) return;
        const y = el.getBoundingClientRect().top + window.scrollY - 150;
        if (window.lenis) window.lenis.scrollTo(y, { duration: 0.7 });
        else window.scrollTo({ top: y, behavior: 'smooth' });
    };

    // Switching chapter scrolls back to the chapter start. This is both better
    // UX and fixes a blank-panel bug: the chapter's content blocks reveal with
    // `whileInView`, so if the reader was scrolled deep into a long chapter and
    // switched to another, the new (shorter) chapter's blocks could sit above
    // the viewport and never trigger their reveal — leaving an empty panel.
    const selectChapter = (i) => {
        setActive(i);
        requestAnimationFrame(() => {
            const el = sectionRef.current;
            if (!el) return;
            const y = el.getBoundingClientRect().top + window.scrollY - 80;
            if (window.lenis) window.lenis.scrollTo(y, { duration: 0.6 });
            else window.scrollTo({ top: y, behavior: 'smooth' });
        });
    };

    // Publish the active chapter to the shared context so the sticky reading
    // progress chip can cycle its label as the reader moves between phases.
    useEffect(() => {
        setChapter({
            index: active,
            total: tabs.length,
            label: tabs[active]?.label || '',
        });
        return () => clearChapter();
    }, [active, tabs, setChapter, clearChapter]);

    // Sync `active` → URL (replace, not push, so we don't spam browser history)
    useEffect(() => {
        // Skip the initial render (URL already reflects state)
        if (!mountedRef.current) {
            mountedRef.current = true;
            return;
        }
        const next = new URLSearchParams(searchParams);
        if (active === 0) {
            // First tab is the default, drop the param to keep URLs clean
            next.delete('tab');
        } else {
            next.set('tab', slugs[active]);
        }
        setSearchParams(next, { replace: true });
    }, [active]);

    // Sync URL → `active` (handles back/forward navigation from the browser)
    useEffect(() => {
        const urlSlug = searchParams.get('tab');
        const urlIdx = urlSlug ? slugs.indexOf(urlSlug) : 0;
        if (urlIdx >= 0 && urlIdx !== active) {
            setActive(urlIdx);
        }
    }, [searchParams]);

    // Allow other blocks (e.g. a storyboard stage) to jump to a chapter by label.
    useEffect(() => {
        const onGoto = (e) => {
            const idx = tabs.findIndex((t) => t.label === e.detail?.label);
            if (idx >= 0) setActive(idx);
        };
        window.addEventListener('cs-goto-tab', onGoto);
        return () => window.removeEventListener('cs-goto-tab', onGoto);
    }, [tabs]);

    const { slug } = useParams();
    const bannerOffset = (slug || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % GENERIC_BANNERS.length;
    const bannerFor = (i) => GENERIC_BANNERS[(i + bannerOffset) % GENERIC_BANNERS.length];
    const heroImage = tabs[active].hero || bannerFor(active);
    const summary = tabs[active].summary || autoBlurb(tabs[active].blocks);
    const thumbs = tabs.map((t, i) => t.thumb || t.hero || bannerFor(i));

    return (
        <div ref={sectionRef} className="mb-8 md:mb-12">
            <ChapterTopBar
                active={active}
                total={tabs.length}
                hasPrev={active > 0}
                hasNext={active < tabs.length - 1}
                onPrev={() => selectChapter(active - 1)}
                onNext={() => selectChapter(active + 1)}
            />

            <ChapterCover
                active={active}
                chapter={tabs[active]}
                summary={summary}
                heroImage={heroImage}
                highlights={tabs[active].highlights}
                onExplore={exploreChapter}
            />

            <div ref={contentRef}>
                <AnimatePresence mode="wait">
                    <motion.div
                        key={active}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
                        data-testid={CASE_STUDY.tabPanel(active)}
                        role="tabpanel"
                    >
                        {tabs[active].blocks.map((b, j) =>
                            createElement(RichBlock, { key: `${active}-${j}`, block: b })
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            <ChapterFilmstrip tabs={tabs} active={active} onSelect={selectChapter} thumbs={thumbs} />
        </div>
    );
};

// ── Sub-tabs, folder/tab style nested tabs. The active tab visually
// connects into the content panel below it (shared surface, no bottom edge),
// while inactive tabs sit behind it in a dimmer surface. Reads as a physical
// tabbed folder, the metaphor makes grouped variants (e.g. four visual-design
// modules under a single Visual design section) instantly scannable.
const SubTabs = ({ heading, tabs }) => {
    const [active, setActive] = useState(0);
    const rootRef = useRef(null);

    // Respond to a cross-block jump (e.g. a storyboard stage → its module tab).
    // `window.__csPendingSubtab` covers the case where this SubTabs mounts only
    // after its parent chapter becomes active; the event covers the live case.
    useEffect(() => {
        const apply = (label) => {
            const idx = tabs.findIndex((t) => t.label === label);
            if (idx < 0) return false;
            setActive(idx);
            setTimeout(() => {
                const el = rootRef.current;
                if (!el) return;
                const y = el.getBoundingClientRect().top + window.scrollY - 96;
                if (window.lenis) window.lenis.scrollTo(y, { duration: 0.6 });
                else window.scrollTo({ top: y, behavior: 'smooth' });
            }, 160);
            return true;
        };
        if (window.__csPendingSubtab && apply(window.__csPendingSubtab)) {
            window.__csPendingSubtab = null;
        }
        const onGoto = (e) => {
            if (apply(e.detail?.label)) window.__csPendingSubtab = null;
        };
        window.addEventListener('cs-goto-subtab', onGoto);
        return () => window.removeEventListener('cs-goto-subtab', onGoto);
    }, [tabs]);

    return (
        <div ref={rootRef} className="mb-8 md:mb-12">
            {heading && (
                <h3 className="mb-4 font-display text-3xl md:text-4xl text-[var(--ink)]">
                    {heading}
                    <span className="text-[var(--accent)]">.</span>
                </h3>
            )}

            {/* Tab strip, sits directly on top of the panel with no gap so the
                 active tab merges into it. Overflow-x scroll on narrow screens.
                 The relative wrapper hosts a right-edge fade so any scroll
                 overflow reads as a fade, not a hard chop. */}
            <div className="relative">
                <div
                    role="tablist"
                    aria-label={heading}
                    className="relative flex items-end gap-1 overflow-x-auto px-2 -mb-px"
                >
                    {tabs.map((t, i) => {
                        const isActive = active === i;
                        return (
                            <button
                                key={i}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                onClick={() => setActive(i)}
                                data-testid={`sub-tab-trigger-${i}`}
                                data-cursor="hover"
                                className={`shrink-0 relative px-5 md:px-6 rounded-t-[14px] font-mono-brand text-[11px] md:text-[11.5px] uppercase tracking-[0.18em] whitespace-nowrap transition-all border border-b-0 ${
                                    isActive
                                        ? 'bg-[var(--surface)] border-[var(--line-2)] text-[var(--ink)] py-3 md:py-3.5 z-10 shadow-[0_-3px_10px_rgba(0,0,0,0.04)]'
                                        : 'bg-[var(--surface-2)] border-[var(--line)] text-[var(--ink-2)] py-2.5 md:py-3 hover:text-[var(--ink)] hover:bg-[var(--surface)]/60 mt-1'
                                }`}
                            >
                                {t.label}
                                {isActive && (
                                    <motion.span
                                        layoutId="sub-tab-underline"
                                        className="absolute left-3 right-3 bottom-[-1px] h-[2px] bg-[var(--accent)]"
                                        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                                    />
                                )}
                            </button>
                        );
                    })}
                    {/* fills the space to the right of the last tab so the panel
                         edge lines up cleanly */}
                    <span
                        aria-hidden
                        className="flex-1 min-w-[8px] border-b border-[var(--line-2)]"
                    />
                </div>
                {/* Right-edge fade, signals horizontal scrollability on narrow
                     viewports instead of clipping a tab label mid-word. */}
                <span
                    aria-hidden
                    className="pointer-events-none absolute top-0 right-0 h-full w-10 bg-gradient-to-l from-[var(--bg)] to-transparent"
                />
            </div>

            {/* Content panel, inherits the same surface as the active tab */}
            <div className="relative rounded-b-[18px] rounded-tr-[18px] border border-[var(--line-2)] bg-[var(--surface)] p-6 md:p-8">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={active}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
                        data-testid={`sub-tab-panel-${active}`}
                        role="tabpanel"
                    >
                        {tabs[active].blocks.map((b, j) =>
                            createElement(RichBlock, { key: `${active}-${j}`, block: b })
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
};

// ── Accordion, progressive disclosure for dense sub-sections. ──────────────

const AccordionItem = ({ it, i, open, onToggle }) => (
    <div className="border-b border-[var(--line)] last:border-b-0">
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            data-testid={CASE_STUDY.accordionTrigger(i)}
            data-cursor="hover"
            className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left hover:bg-white/[0.02] transition-colors"
        >
            <div className="flex items-center gap-4 min-w-0">
                <span className="font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--accent)] tabular-nums shrink-0">
                    {String(i + 1).padStart(2, '0')}
                </span>
                <div className="min-w-0">
                    <h4 className="font-display text-xl md:text-2xl text-[var(--ink)] leading-tight">
                        {it.title}
                    </h4>
                    {it.summary && (
                        <p className="mt-1 font-sans-brand text-[12.5px] leading-[1.55] text-[var(--ink-3)]">
                            {it.summary}
                        </p>
                    )}
                </div>
            </div>
            <ChevronDown
                size={18}
                className={`shrink-0 text-[var(--ink-3)] transition-transform duration-300 ${
                    open ? 'rotate-180 text-[var(--accent)]' : ''
                }`}
            />
        </button>
        <AnimatePresence initial={false}>
            {open && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
                    data-testid={CASE_STUDY.accordionPanel(i)}
                    className="overflow-hidden"
                >
                    <div className="px-5 md:px-6 pb-5 md:pb-7 pt-2 border-t border-[var(--line)]">
                        {it.blocks.map((b, j) =>
                            createElement(RichBlock, { key: `${i}-${j}`, block: b })
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    </div>
);

const RichAccordion = ({ items, defaultOpen = 0 }) => {
    const [openIdx, setOpenIdx] = useState(defaultOpen);
    return (
        <div className="mb-6 md:mb-8 rounded-2xl border border-[var(--line)] bg-[var(--surface)] overflow-hidden">
            {items.map((it, i) => (
                <AccordionItem
                    key={i}
                    it={it}
                    i={i}
                    open={openIdx === i}
                    onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
                />
            ))}
        </div>
    );
};

// ── Dispatcher ──────────────────────────────────────────────────────────────
// Renders a single rich block by kind. Recursively used by RichTabs & RichAccordion.

export const RichBlock = ({ block: b }) => {
    switch (b.kind) {
        case 'section':
            return (
                <RichHeading
                    heading={b.heading}
                    subheading={b.subheading}
                    paragraph={b.paragraph}
                />
            );
        case 'image':
            return <RichImage src={b.src} caption={b.caption} size={b.size} variant={b.variant} />;
        case 'image-grid':
            return <BlockImageGrid images={b.images} />;
        case 'goals':
            return <BlockGoals heading={b.heading} items={b.items} />;
        case 'grid-cards':
            return <BlockGridCards items={b.items} />;
        case 'color-swatches':
            return <BlockColorSwatches items={b.items} />;
        case 'quotes':
            return <BlockQuotes items={b.items} />;
        case 'meta-list':
            return <BlockMetaList items={b.items} />;
        case 'persona':
            return <BlockPersona block={b} />;
        case 'principle':
            return <BlockPrinciple block={b} />;
        case 'excluded':
            return <BlockExcluded items={b.items} />;
        case 'phase-hero':
            return <BlockPhaseHero block={b} />;
        case 'bullets':
            return <BlockBullets heading={b.heading} items={b.items} columns={b.columns} />;
        case 'storyboard':
            return <BlockStoryboard heading={b.heading} scenario={b.scenario} stages={b.stages} />;
        case 'tabs':
            return <RichTabs tabs={b.tabs} />;
        case 'accordion':
            return <RichAccordion items={b.items} defaultOpen={b.defaultOpen ?? 0} />;
        case 'sub-tabs':
            return <SubTabs heading={b.heading} tabs={b.tabs} />;
        case 'cloudhub-ia':
            return <CloudHubIA />;
        case 'cloudhub-impact':
            return <CloudHubImpactDeck />;
        case 'cloudhub-journey-map':
            return <CloudHubJourneyMap />;
        case 'cloudhub-empathy-map':
            return <CloudHubEmpathyMap />;
        case 'engagedly-empathy-map':
            return <EngagedlyEmpathyMap />;
        case 'engagedly-workshop-board':
            return <EngagedlyWorkshopBoard />;
        case 'engagedly-rough-sketch':
            return <EngagedlyRoughSketch />;
        case 'engagedly-traits':
            return <EngagedlyTraits />;
        default:
            return null;
    }
};
